/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { isRemoteMode } from '../config/repository';
import { apiFetch } from '../data/ApiModuleRepository';

export interface HeartbeatState {
  unreadCount: number;
  newNotifications: any[];
  pendingMenuRequests: number;
  lastPulse: number;
}

export function useHeartbeat(enabled: boolean, intervalMs: number = 15000) {
  const [state, setState] = useState<HeartbeatState>({
    unreadCount: 0,
    newNotifications: [],
    pendingMenuRequests: 0,
    lastPulse: 0,
  });
  
  const sinceRef = useRef<number>(Date.now());
  const prevPendingRef = useRef<number>(-1);

  const beat = useCallback(async () => {
    if (!isRemoteMode() || !enabled || intervalMs <= 0) return;
    try {
      const res = await apiFetch<any>(`/api/pulse?since=${sinceRef.current}`);
      const data = res.data ?? res;
      
      const newNotifs = data.newNotifications || [];
      const unread = data.unreadCount ?? 0;
      const pendingMenu = data.pendingMenuRequests ?? 0;
      const serverTime = data.serverTime ?? Date.now();

      const pendingChanged = prevPendingRef.current >= 0 && prevPendingRef.current !== pendingMenu;
      prevPendingRef.current = pendingMenu;
      sinceRef.current = serverTime;

      setState({
        unreadCount: unread,
        newNotifications: newNotifs,
        pendingMenuRequests: pendingMenu,
        lastPulse: Date.now(),
      });

      // Fire a generic PULSE_UPDATE event on every successful heartbeat
      window.dispatchEvent(new CustomEvent('PULSE_UPDATE', { detail: { unreadCount: unread, pendingMenuRequests: pendingMenu } }));
      
      // Fire menu-data-updated to trigger refresh in MenuManageModule
      window.dispatchEvent(new Event('menu-data-updated'));

      if (pendingChanged) {
        window.dispatchEvent(new CustomEvent('PULSE_MENU_STATUS_CHANGED', { detail: { pendingMenu } }));
      }

      if (newNotifs.length > 0) {
        window.dispatchEvent(new CustomEvent('PULSE_NEW_NOTIFICATIONS', { detail: { notifications: newNotifs, unreadCount: unread } }));
      }
    } catch {
      // Background failures should be completely silent to the user
    }
  }, [enabled, intervalMs]);

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;
    beat();
    const id = setInterval(beat, intervalMs);
    return () => clearInterval(id);
  }, [beat, enabled, intervalMs]);

  return { ...state, beat };
}
